import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BsBagDash } from "react-icons/bs";
import "./HomeRoutineSection.css";
import ViewAllLink from "../../components/ViewAllLink";
import { useCurrency } from "../../Context/CurrencyContext";
import { useCart } from "../../Context/CartContext";

const products = [
  {
    id: 4,
    img: "/images/dailyroutine1.webp",
    brand: "BKIND",
    title: "Algae Peel-Off Mask",
    price: 999,
    priceValue: 999,
  },
  {
    id: 5,
    img: "/images/dailyroutine2.webp",
    brand: "MOKOSH",
    title: "Active toning essence",
    price: 555,
    priceValue: 555,
  },
  {
    id: 6,
    img: "/images/dailyroutine3.webp",
    brand: "MOKOSH",
    title: "Figa smoothing face cream",
    price: 650,
    priceValue: 650,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT LOGIC
//
//  All cards are fully visible with complete content
//  No arrow buttons - only drag/slide functionality
//
//  currentCardIndex (0-based):
//    0  →  translateX(+724px)  only card-1 visible
//    1  →  translateX(+362px)  card-1 + card-2 visible
//    2  →  translateX(   0px)  all 3 cards visible
// ─────────────────────────────────────────────────────────────────────────────
const CARD_WIDTH = 350;
const GAP        = 12;
const CARD_STEP  = CARD_WIDTH + GAP; // 362

// translateX offsets per card index (0-based)
const OFFSETS = [
  CARD_STEP * 2, // index 0 → 724px  (only card-1 visible)
  CARD_STEP * 1, // index 1 → 362px  (card-1 + card-2)
  0,             // index 2 →   0px  (all 3 cards)
];

const TRANSITION_MS = 800;

// Video timestamps where each card's product starts being used
const PHASE_STARTS = [0, 6.2, 14.3]; // card-0, card-1, card-2

export default function HomeRoutineSection() {
  const { formatPrice } = useCurrency();
  const { addToCart, openCart } = useCart();

  // ── Refs ───────────────────────────────────────────────────────────────────
  const trackRef           = useRef(null);
  const videoRef           = useRef(null);
  const sectionRef         = useRef(null);
  const videoPlayAttempted = useRef(false);
  const transitionInProg   = useRef(false);
  const currentIndexRef    = useRef(0);
  const offsetRef          = useRef(OFFSETS[0]);
  const isDragging         = useRef(false);
  const dragStartX         = useRef(0);
  const dragStartOffset    = useRef(0);

  // ── State ──────────────────────────────────────────────────────────────────
  const [visibleCount, setVisibleCount] = useState(1);
  const [isPlaying,    setIsPlaying]    = useState(false);
  const [videoError,   setVideoError]   = useState(false);
  const [hoveredTitle, setHoveredTitle] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 2000);
  };

  // ── Track helpers ──────────────────────────────────────────────────────────
  const snapTo = (offset) => {
    if (!trackRef.current) return;
    transitionInProg.current = false;
    trackRef.current.style.transition = "none";
    offsetRef.current = offset;
    trackRef.current.style.transform = `translateX(${offset}px)`;
  };

  const slideTo = (targetOffset) => {
    if (!trackRef.current) return;
    if (offsetRef.current === targetOffset) return;

    transitionInProg.current = true;
    trackRef.current.style.transition =
      `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
    offsetRef.current = targetOffset;
    trackRef.current.style.transform = `translateX(${targetOffset}px)`;

    setTimeout(() => {
      if (trackRef.current) trackRef.current.style.transition = "";
      transitionInProg.current = false;
    }, TRANSITION_MS);
  };

  // ── Core navigation — go to card by 0-based index ─────────────────────────
  const goToCard = (rawIndex) => {
    const index = Math.max(0, Math.min(2, rawIndex));

    currentIndexRef.current = index;
    setVisibleCount(index + 1);

    setTimeout(() => slideTo(OFFSETS[index]), 30);

    if (videoRef.current) {
      videoRef.current.currentTime = PHASE_STARTS[index];
      if (videoRef.current.paused) {
        videoRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(() => {});
      }
    }
  };

  // ── Mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    currentIndexRef.current = 0;
    snapTo(OFFSETS[0]);
  }, []);

  // ── Video time-sync ────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncToTime = (t) => {
      let targetIndex;
      if      (t < PHASE_STARTS[1]) targetIndex = 0;
      else if (t < PHASE_STARTS[2]) targetIndex = 1;
      else                           targetIndex = 2;

      if (currentIndexRef.current === targetIndex) return;
      currentIndexRef.current = targetIndex;
      setVisibleCount(targetIndex + 1);
      setTimeout(() => slideTo(OFFSETS[targetIndex]), 30);
    };

    const onTimeUpdate = () => syncToTime(video.currentTime);
    const onEnded      = () => {
      currentIndexRef.current = 0;
      setVisibleCount(1);
      snapTo(OFFSETS[0]);
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended",      onEnded);
    video.addEventListener("seeked",     onTimeUpdate);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended",      onEnded);
      video.removeEventListener("seeked",     onTimeUpdate);
    };
  }, []);

  // ── Auto-play video ────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoPlayAttempted.current) return;
    videoPlayAttempted.current = true;
    video.preload = "auto";

    const tryPlay = async () => {
      try { await video.play(); setIsPlaying(true); }
      catch { setVideoError(true); }
    };

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      const onCanPlay = () => { tryPlay(); video.removeEventListener("canplay", onCanPlay); };
      video.addEventListener("canplay", onCanPlay);
      setTimeout(() => video.removeEventListener("canplay", onCanPlay), 5000);
    }

    return () => { video.pause(); };
  }, []);

  // ── Drag functionality ───────────────────────────────────────────────────
  const onMouseDown = (e) => {
    e.preventDefault();
    isDragging.current    = true;
    dragStartX.current    = e.clientX;
    dragStartOffset.current = offsetRef.current;
  };

  const onMouseMove = (e) => {
    if (!isDragging.current || !trackRef.current) return;
    e.preventDefault();
    const delta = e.clientX - dragStartX.current;
    const next  = Math.max(OFFSETS[2], Math.min(OFFSETS[0], dragStartOffset.current + delta));
    offsetRef.current = next;
    trackRef.current.style.transform = `translateX(${next}px)`;
  };

  const onMouseUp = () => { isDragging.current = false; };

  // ── Video toggle ───────────────────────────────────────────────────────────
  const togglePlay = async () => {
    if (!videoRef.current || videoError) return;
    try {
      if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); }
      else           { await videoRef.current.play(); setIsPlaying(true); }
    } catch { /* ignore */ }
  };

  // ── Rewind button on each card ───────────────────────────────────────────
  const handleRewindClick = (e, cardIndex) => {
    e.preventDefault();
    e.stopPropagation();
    goToCard(cardIndex + 1);
  };

  // ── Add to cart ────────────────────────────────────────────────────────────
  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      addToCart({
        id: product.id, productId: product.id,
        title: product.title, name: product.title,
        price: product.price, image: product.img,
        brand: product.brand, quantity: 1, qty: 1,
        uniqueId:     `${product.id}-routine-${Date.now()}`,
        basePrice:    product.price,
        baseCurrency: "USD",
      }, 1);
      openCart();
      showNotification(`${product.title} added to cart!`);
    } catch {
      showNotification("Failed to add to cart. Please try again.", "error");
    }
  };

  const handleImageClick = (e) => {
    if (isDragging.current) { e.preventDefault(); return false; }
    return true;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="routine-wrapper" ref={sectionRef}>
      <h2 className="routine-heading">Your Daily Routine</h2>

      <div className="routine-section">

        {/* Background video */}
        {!videoError ? (
          <video
            ref={videoRef}
            className="routine-bg-video"
            poster="/images/video-poster.jpg"
            onError={() => setVideoError(true)}
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="/videos/5thsectionvideo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div
            className="routine-bg-fallback"
            style={{
              width: "100%", height: "100%", background: "#faece6",
              borderRadius: "15px", display: "flex", alignItems: "center",
              justifyContent: "center", color: "#151515", fontSize: "18px",
            }}
          >
            <p>Discover Your Daily Routine</p>
          </div>
        )}

        {/* Play / Pause */}
        {!videoError && (
          <button
            className={`routine-control-btn ${isPlaying ? "pause" : "play"}`}
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause video" : "Play video"}
          />
        )}

        {/* Product slider - No arrow buttons */}
        <div className="routine-slider">
          <div
            className="routine-slider-track"
            ref={trackRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onDragStart={(e) => e.preventDefault()}
          >
            {products.map((product, index) => {
              const isVisible = index < visibleCount;

              return (
                <div
                  className="routine-card"
                  key={product.id}
                  style={{
                    opacity:       isVisible ? 1 : 0,
                    pointerEvents: isVisible ? "auto" : "none",
                    transition:    "opacity 0.45s ease",
                  }}
                >
                  <Link
                    to={`/product-details/${product.id}`}
                    className="card-image-link"
                    onClick={handleImageClick}
                    draggable="false"
                  >
                    <div className="card-image-container">
                      <img
                        src={product.img}
                        alt={product.title}
                        draggable="false"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                    </div>
                  </Link>

                  <div className="card-content">
                    <div className="card-text">
                      <p className="card-brand">{product.brand}</p>
                      <h4
                        className={`card-title ${hoveredTitle === product.id ? "hovered" : ""}`}
                        onMouseEnter={() => setHoveredTitle(product.id)}
                        onMouseLeave={() => setHoveredTitle(null)}
                      >
                        <Link
                          to={`/product-details/${product.id}`}
                          className="title-link"
                          onClick={(e) => { if (isDragging.current) e.preventDefault(); }}
                        >
                          {product.title}
                        </Link>
                      </h4>
                      <span className="card-price">{formatPrice(product.price)}</span>
                    </div>

                    <div className="card-actions">
                      {/* Add to cart */}
                      <button
                        className="add-cart-btn"
                        onClick={(e) => handleAddToCart(e, product)}
                        aria-label="Add to cart"
                      >
                        <BsBagDash className="cart-icon" />
                      </button>

                      {/* Rewind button */}
                      <button
                        className="shoppable-video__rewind-button"
                        aria-label={`Go to next product`}
                        onClick={(e) => handleRewindClick(e, index)}
                      >
                        <svg height="28" viewBox="0 0 25 25" width="28" xmlns="http://www.w3.org/2000/svg">
                          <path d="m21.128 6.313c-.346 0-.705.12-1.069.357l-6.646 4.32c-.549.356-.866.864-.904 1.403v-4.393c0-1.165-.693-1.687-1.38-1.687-.346 0-.705.12-1.069.357l-6.646 4.32c-.585.38-.92.93-.92 1.509s.335 1.129.92 1.509l6.646 4.32c.364.237.724.357 1.069.357.417 0 .783-.18 1.033-.506.227-.297.347-.705.347-1.181v-4.395c.038.539.355 1.047.904 1.403l6.646 4.32c.364.237.724.357 1.069.357.417 0 .783-.18 1.033-.506.227-.297.347-.705.347-1.181v-8.996c0-1.165-.694-1.687-1.38-1.687zm-9.62 10.687c0 .251-.05.454-.141.573-.143.186-.43.136-.764-.082l-6.646-4.32c-.3-.195-.465-.433-.465-.671s.165-.476.465-.671l6.646-4.32c.193-.126.38-.195.524-.195.343 0 .38.48.38.687v8.999zm10 0c0 .251-.05.454-.141.573-.144.186-.429.136-.764-.082l-6.646-4.32c-.3-.195-.465-.433-.465-.671s.165-.476.465-.671l6.646-4.32c.193-.126.38-.195.524-.195.343 0 .38.48.38.687v8.999z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* View All */}
      <div className="routine-view-all">
        <Link to="/daily-routine" className="view-all-bold">
          <ViewAllLink />
          <span className="product-text-wrapper">
            <span className="product-text">PRODUCTS</span>
            <span className="product-underline"></span>
          </span>
        </Link>
      </div>

      {/* Cart notification */}
      {notification.show && (
        <div className={`cart-message ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
}